"use memo"

import {
  AutopilotTaskDto,
  TaskBlockerBlockerType,
  TaskCategoryTaskType,
} from '@urbanx/agx.activities.client'

export type ExchangeTaskFamily = 'solicitor' | 'agent' | 'contract'

export interface ExchangeTaskGroup {
  family: ExchangeTaskFamily
  informationTask?: AutopilotTaskDto
  contractDatesTask?: AutopilotTaskDto
  tasks: AutopilotTaskDto[]
}

export const exchangeTaskTypes = [
  TaskCategoryTaskType.SolicitorExchangeInformation,
  TaskCategoryTaskType.SolicitorExchangeContractDates,
  TaskCategoryTaskType.AgentExchangeInformation,
  TaskCategoryTaskType.AgentExchangeContractDates,
  TaskCategoryTaskType.ContractExchangeInformation,
  TaskCategoryTaskType.ContractExchangeContractDates,
] as const

export const isExchangeTaskType = (taskType?: TaskCategoryTaskType) =>
  !!taskType &&
  exchangeTaskTypes.includes(taskType as (typeof exchangeTaskTypes)[number])

export const hasMarkedAsExchangeBlocker = (task?: AutopilotTaskDto) =>
  task?.blockers?.some(
    (blocker) =>
      blocker.blockerType === TaskBlockerBlockerType.MarkedAsExchanged
  ) === true

export const requiresExchangeEvidence = (group: ExchangeTaskGroup) =>
  group.family !== 'contract' &&
  group.tasks.some((task) => task.isPending || hasMarkedAsExchangeBlocker(task))

export const getExchangeTaskFamily = (
  taskType?: TaskCategoryTaskType
): ExchangeTaskFamily | undefined => {
  switch (taskType) {
    case TaskCategoryTaskType.SolicitorExchangeInformation:
    case TaskCategoryTaskType.SolicitorExchangeContractDates:
      return 'solicitor'
    case TaskCategoryTaskType.AgentExchangeInformation:
    case TaskCategoryTaskType.AgentExchangeContractDates:
      return 'agent'
    case TaskCategoryTaskType.ContractExchangeInformation:
    case TaskCategoryTaskType.ContractExchangeContractDates:
      return 'contract'
    default:
      return undefined
  }
}

export const getExchangeTaskGroups = (
  tasks: AutopilotTaskDto[]
): ExchangeTaskGroup[] => {
  const groups = new Map<string, ExchangeTaskGroup>()

  tasks.forEach((task) => {
    const family = getExchangeTaskFamily(task.category?.taskType)
    if (!family) return

    const groupKey = `${task.contractId ?? task.campaignId}-${family}`
    const group =
      groups.get(groupKey) ??
      ({
        family,
        tasks: [],
      } satisfies ExchangeTaskGroup)

    if (
      task.category?.taskType ===
        TaskCategoryTaskType.SolicitorExchangeInformation ||
      task.category?.taskType === TaskCategoryTaskType.AgentExchangeInformation ||
      task.category?.taskType === TaskCategoryTaskType.ContractExchangeInformation
    ) {
      group.informationTask = task
    }

    if (
      task.category?.taskType ===
        TaskCategoryTaskType.SolicitorExchangeContractDates ||
      task.category?.taskType === TaskCategoryTaskType.AgentExchangeContractDates ||
      task.category?.taskType === TaskCategoryTaskType.ContractExchangeContractDates
    ) {
      group.contractDatesTask = task
    }

    group.tasks = [group.informationTask, group.contractDatesTask].filter(
      Boolean
    ) as AutopilotTaskDto[]

    groups.set(groupKey, group)
  })

  return [...groups.values()].filter((group) => group.tasks.length > 0)
}

export const getExchangeTaskTitle = (family: ExchangeTaskFamily) => {
  switch (family) {
    case 'solicitor':
      return 'Solicitor Exchange'
    case 'agent':
      return 'Agent Exchange'
    case 'contract':
      return 'Contract Exchange'
  }
}
